<script lang="ts" setup>
import { sortKeysAlphabetically } from '@thecompaniesapi/object-helpers'
import { ApiBox } from '#components'

const props = defineProps({
  hasHeader: {
    default: true,
    type: Boolean,
  },
  levelToOpen: {
    default: 0,
    type: Number,
  },
  loading: {
    default: false,
    type: Boolean,
  },
  maxHeight: {
    default: '',
    type: String,
  },
  response: {
    default: undefined,
    type: [Array, Object] as PropType<ApiResponseResponse>,
  },
  sortKeysAlphabetically: {
    default: true,
    type: Boolean,
  },
})

const { t } = useI18n()

const apiBoxRef = ref<InstanceType<typeof ApiBox>>()

const sortedResponse = computed(() => {
  // Sort object alphabetically
  if (props.sortKeysAlphabetically) {
    try {
      if (typeof props.response === 'object' && props.response !== null) {
        return sortKeysAlphabetically(props.response)
      }
      // Sort all objects in array alphabetically
      else if (Array.isArray(props.response)) {
        return (props.response as any[]).map((item: any) => {
          return typeof item === 'object' && item !== null
            ? sortKeysAlphabetically(item)
            : item
        })
      }
    }
    catch (error) {
      console.error(error)
    }
  }

  return props.response
})

function resetScrollPosition() {
  apiBoxRef.value?.resetScrollPosition()
}

defineExpose({ resetScrollPosition })
</script>

<template>
  <ApiBox
    ref="apiBoxRef"
    class="flex-1 text-left"
    :has-header="hasHeader"
    :max-height="maxHeight"
    :string-to-copy="response || ''"
    :title="t('apiResponse')"
  >
    <template v-if="!loading">
      <div
        v-if="response"
        class="grid grid-cols-1 gap-2 font-mono"
      >
        <ApiResponseLine
          :level="0"
          :value="Array.isArray(response) ? '[' : '{'"
        />

        <ApiResponseLine
          v-for="(objectValue, objectKey) in sortedResponse"
          :key="objectKey"
          :level="1"
          :level-to-open="levelToOpen"
          :object-key="objectKey"
          :value="objectValue as string"
        />

        <ApiResponseLine
          :level="0"
          :value="Array.isArray(response) ? ']' : '}'"
        />
      </div>

      <div
        v-else
        class="flex h-full flex-col justify-center"
      >
        <BaseMessage
          background="black"
          class="-mt-20"
          :description="t('empty.description')"
          power="starwars"
          :title="t('empty.title')"
        />
      </div>
    </template>

    <div
      v-else
      class="flex h-full flex-col items-center justify-center py-10 text-sm uppercase text-gray-900 dark:text-gray-100"
    >
      <BaseSpinner class="mb-5 size-6" />

      {{ t('loadingReponse') }}
    </div>
  </ApiBox>
</template>

<i18n lang="json">
{
  "en": {
    "apiResponse": "API response",
    "empty": {
      "description": "We did not receive any data from the API. Contact us if you think this is an error.",
      "title": "The response is empty"
    },
    "loadingReponse": "Loading response...",
    "response": "Response"
  },
  "es": {
    "apiResponse": "Respuesta de la API",
    "empty": {
      "description": "No recibimos ningún dato de la API. Contacta con nosotros si crees que se trata de un error.",
      "title": "La respuesta está vacía"
    },
    "loadingReponse": "Cargando respuesta...",
    "response": "Respuesta"
  },
  "fr": {
    "apiResponse": "Réponse de l'API",
    "empty": {
      "description": "Nous n'avons reçu aucune donnée de l'API. Contactez-nous si vous pensez qu'il s'agit d'une erreur.",
      "title": "La réponse est vide"
    },
    "loadingReponse": "Chargement de la réponse...",
    "response": "Réponse"
  }
}
</i18n>
